import type { ActiveVehicle } from "../components/chat/VehicleHeaderBar";

/**
 * Merge incoming vehicle data into an existing header vehicle, preserving the more specific
 * variant/description.
 *
 * TRAC-905: the server-built Shop Tires link (`shopUrl`) survives the merge. The incoming link
 * wins; the existing one is kept only while the sizes it was built for are unchanged, and is
 * otherwise cleared so the header re-resolves it from the server rather than showing a link
 * for the wrong size. Previously the spread dropped `incoming.shopUrl`, so a vehicle picked in
 * the selector lost its correct link after any update.
 */
export function mergeVehicle(existing: ActiveVehicle, incoming: ActiveVehicle): ActiveVehicle {
  const keepExistingDesc = existing.description.length >= incoming.description.length;
  let model = existing.model;
  const variant = keepExistingDesc ? (existing.variant || incoming.variant) : (incoming.variant || existing.variant);
  // TRAC-375: If variant is already baked into the existing model name
  // (e.g. agent's model="Camry LE" + YMMV's variant="LE"), prefer the
  // incoming model/variant split to avoid "Camry LE LE" doubling.
  if (variant && model.toLowerCase().endsWith(` ${variant.toLowerCase()}`)) {
    model = incoming.model;
  }
  // TRAC-905 (sweep test 21): an agent card that names ONE axle of the pair the customer
  // picked ("225/40R19" for a 225/40R19 F / 255/35R19 R Giulia) restates the pick, it doesn't
  // change it. Taking it as a new size wrote a stray tireSize next to the pair and dropped the
  // picker's link.
  const restatesAxle = !incoming.frontSize && !incoming.rearSize && isAxleOf(incoming.tireSize, existing);
  const tireSize = restatesAxle ? existing.tireSize : (incoming.tireSize ?? existing.tireSize);
  const frontSize = incoming.frontSize ?? existing.frontSize;
  const rearSize = incoming.rearSize ?? existing.rearSize;
  const existingSizesUnchanged =
    existing.tireSize === tireSize && existing.frontSize === frontSize && existing.rearSize === rearSize;
  return {
    ...existing,
    model,
    variant,
    tireSize,
    staggered: incoming.staggered ?? existing.staggered,
    frontSize,
    rearSize,
    runflat: incoming.runflat ?? existing.runflat,
    shopUrl: incoming.shopUrl ?? (existingSizesUnchanged ? existing.shopUrl : undefined),
    source: incoming.source,
    timestamp: incoming.timestamp,
    description: keepExistingDesc ? existing.description : incoming.description,
  };
}

/**
 * A metric size reduced to width/ratio/diameter, so spellings of the same tire compare equal:
 * "225/40R19", "225/40ZR19", "P225/40R19", "LT245/70R17", "245/70-17". Anything else (flotation,
 * unparseable) falls back to the whitespace-free uppercase string.
 */
function sizeKey(size: string): string {
  const s = size.replace(/\s+/g, "").toUpperCase();
  const m = s.match(/^(?:P|LT|ST|T)?(\d{3})\/(\d{2})(?:Z?R|-)(\d{2}(?:\.\d)?)/);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : s;
}

/** Is `size` one axle of `v`'s staggered pair? */
function isAxleOf(size: string | undefined, v: ActiveVehicle): boolean {
  if (!size || !v.frontSize || !v.rearSize) return false;
  const s = sizeKey(size);
  return s === sizeKey(v.frontSize) || s === sizeKey(v.rearSize);
}

/**
 * The header history after `vehicle` arrives: merged into the entry for the same vehicle,
 * else appended. ChatInterface's upsertVehicle runs exactly this.
 */
export function upsertVehicleHistory(history: ActiveVehicle[], vehicle: ActiveVehicle): ActiveVehicle[] {
  const existingIdx = history.findIndex((v) => isSameVehicle(v, vehicle));
  if (existingIdx < 0) return [...history, vehicle];
  const updated = [...history];
  updated[existingIdx] = mergeVehicle(updated[existingIdx], vehicle);
  return updated;
}

/** The header's active vehicle after `vehicle` arrives (ChatInterface's upsertVehicle). */
export function upsertActiveVehicle(active: ActiveVehicle | null, vehicle: ActiveVehicle): ActiveVehicle {
  return active && isSameVehicle(active, vehicle) ? mergeVehicle(active, vehicle) : vehicle;
}

const normalizeDescription = (desc: string) => desc.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Do two header entries describe the same vehicle? Matches by year+make+normalized-model so
 * YMMV and TDG card entries merge. parseVehicleDescription splits "X5 xDrive40i" into
 * model="X5", variant="xDrive40i" but the YMMV API keeps model="X5 xDrive40i", variant="Runflat
 * Tire"; combining model+variant and prefix matching handles that mismatch.
 *
 * TRAC-918: identical descriptions are always the same vehicle, so an entry whose make was
 * mis-split from its description still merges with the picker's entry (and keeps its shopUrl)
 * instead of becoming a second, broken vehicle in the dropdown.
 */
export function isSameVehicle(a: ActiveVehicle, b: ActiveVehicle): boolean {
  if (a.year !== b.year) return false;
  if (a.description && b.description && normalizeDescription(a.description) === normalizeDescription(b.description)) {
    return true;
  }
  if (a.make.toLowerCase() !== b.make.toLowerCase()) return false;
  const fullA = [a.model, a.variant].filter(Boolean).join(" ").toLowerCase();
  const fullB = [b.model, b.variant].filter(Boolean).join(" ").toLowerCase();
  if (fullA === fullB) return true;
  const [shorter, longer] = fullA.length <= fullB.length ? [fullA, fullB] : [fullB, fullA];
  return longer.startsWith(shorter + " ");
}
